import React from 'react';

/**
 * A button that opens a list to pick from, drawn like the property picker.
 * Options are { key, primary, secondary, group }; a group's name heads the
 * first option of it. A `multiple` list stays open so several can be ticked.
 *
 * The menu is positioned against the viewport, as the property picker's is:
 * the compose card would clip it otherwise.
 */
export default function Dropdown({
    value, placeholder, options, isOn, onPick, ariaLabel, searchPlaceholder, emptyText, multiple = false,
}) {
    const [open, setOpen] = React.useState(false);
    const [box, setBox] = React.useState(null);
    const [query, setQuery] = React.useState('');
    const buttonRef = React.useRef(null);
    const rootRef = React.useRef(null);

    const place = () => {
        const rect = buttonRef.current.getBoundingClientRect();
        const below = window.innerHeight - rect.bottom;
        const anchor = below < 200 && rect.top > below
            ? { bottom: window.innerHeight - rect.top + 5 }
            : { top: rect.bottom + 5 };

        setBox({ ...anchor, left: rect.left, width: rect.width });
    };

    React.useEffect(() => {
        if (!open) return undefined;

        // Asked of the event rather than stopped in a handler, for the reason
        // given in PropertyPicker.
        const inside = (event) => rootRef.current && rootRef.current.contains(event.target);
        const closeOutside = (event) => { if (!inside(event)) setOpen(false); };
        const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
        const onResize = () => setOpen(false);

        window.addEventListener('scroll', closeOutside, true);
        window.addEventListener('resize', onResize);
        document.addEventListener('mousedown', closeOutside);
        document.addEventListener('keydown', onKey);

        return () => {
            window.removeEventListener('scroll', closeOutside, true);
            window.removeEventListener('resize', onResize);
            document.removeEventListener('mousedown', closeOutside);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const toggle = () => {
        if (!open) {
            place();
            setQuery('');
        }
        setOpen(!open);
    };

    const needle = query.trim().toLowerCase();
    const shown = needle
        ? options.filter((option) => (option.primary + ' ' + (option.secondary || '')).toLowerCase().indexOf(needle) !== -1)
        : options;

    return (
        <div className="mm-pick" ref={rootRef}>
            <button
                type="button"
                ref={buttonRef}
                className={'mm-pick__button' + (open ? ' is-open' : '')}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                onClick={toggle}
            >
                <span className={'mm-pick__value' + (value ? '' : ' mm-pick__value--empty')}>{value || placeholder}</span>
                <svg className={'mm-pick__caret' + (open ? ' is-open' : '')} width="11" height="11"
                    viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path d="M3 4.3 L5.5 6.8 L8 4.3"/>
                </svg>
            </button>

            {open && box ? (
                <div className="mm-pick__menu" role="listbox" style={box}>
                    {searchPlaceholder ? (
                        <input
                            className="mm-pick__search"
                            autoFocus
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder={searchPlaceholder}
                        />
                    ) : null}

                    {shown.map((option, i) => {
                        const on = isOn(option.key);
                        const heading = option.group && (i === 0 || shown[i - 1].group !== option.group);

                        return (
                            <React.Fragment key={option.key}>
                                {heading ? <div className="mm-pick__group">{option.group}</div> : null}
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={on}
                                    className={'mm-pick__item' + (on ? ' is-on' : '')}
                                    onClick={() => { onPick(option.key); if (!multiple) setOpen(false); }}
                                >
                                    <span className="mm-pick__text">
                                        <span className="mm-pick__primary">{option.primary}</span>
                                        {option.secondary ? <span className="mm-pick__secondary">{option.secondary}</span> : null}
                                    </span>
                                    {on ? (
                                        <svg className="mm-pick__check" width="12" height="12" viewBox="0 0 12 12"
                                            fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                                            <path d="M2.4 6.3 L4.8 8.7 L9.6 3.5"/>
                                        </svg>
                                    ) : null}
                                </button>
                            </React.Fragment>
                        );
                    })}

                    {shown.length === 0 ? <div className="mm-pick__empty">{emptyText}</div> : null}
                </div>
            ) : null}
        </div>
    );
}
